/**
 * Kitchen size buckets (Phase 5A). The brief asks for a bucketed size
 * question ("Under 80 sq. ft.", "80-120 sq. ft.", etc.) rather than asking
 * for an exact number up front, "structured so these ranges can later be
 * replaced or supplemented with an exact square-foot input."
 *
 * This is that structure: a single small data table, shared by the
 * `kitchen_size_range` question definition (below, in
 * `questions/kitchen.ts`) and the estimator app's Step 2 UI
 * (`estimator-app/src/components/steps/Step2RoomSize.tsx`). The engine
 * itself never sees a bucket - every bucket resolves to a representative
 * `roomSize` (a plain `Dimensioned` sqft value), which is the exact-sqft
 * input the engine has always accepted. Swapping this bucketed question
 * for a direct numeric one later (or, as Step 2 already does, offering
 * both and letting the user pick) requires no engine or pricing change at
 * all - only a different way of producing the same `room_size` answer.
 */
export interface KitchenSizeRange {
  id: string;
  label: string;
  /** Representative square footage used to drive the calculation when this bucket is picked. */
  representativeSqft: number;
  min: number;
  /** null = no upper bound ("180+ sq. ft."). */
  max: number | null;
}

export const KITCHEN_SIZE_RANGES: KitchenSizeRange[] = [
  { id: "under_80", label: "Under 80 sq. ft.", representativeSqft: 65, min: 0, max: 80 },
  { id: "80_120", label: "80–120 sq. ft.", representativeSqft: 100, min: 80, max: 120 },
  { id: "120_180", label: "120–180 sq. ft.", representativeSqft: 150, min: 120, max: 180 },
  { id: "180_plus", label: "180+ sq. ft.", representativeSqft: 220, min: 180, max: null }
];

export function findKitchenSizeRange(id: string | undefined): KitchenSizeRange | undefined {
  return KITCHEN_SIZE_RANGES.find((r) => r.id === id);
}

/** Finds the bucket a given exact square footage falls into, for keeping the bucket answer and an exact value in sync. */
export function kitchenSizeRangeForSqft(sqft: number): KitchenSizeRange | undefined {
  return KITCHEN_SIZE_RANGES.find((r) => sqft >= r.min && (r.max === null || sqft < r.max)) ?? KITCHEN_SIZE_RANGES[KITCHEN_SIZE_RANGES.length - 1];
}
